
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Target, Map, BarChart3, ArrowRight } from "lucide-react";

const CareerArtifactsPage = () => {
  const artifacts = [
    {
      title: "Big Sheet™",
      description: "Document your career journey and uncover patterns that reveal your unique professional story.",
      icon: FileText,
      path: "/artifacts/big-sheet",
      color: "text-accent-blue",
      bgGradient: "from-accent-blue/10 to-accent-blue/5",
      benefit: "Get clarity on your career direction",
      time: "45 min"
    },
    {
      title: "Power Moves™",
      description: "Plan strategic career actions that will accelerate your professional growth.",
      icon: Map,
      path: "/artifacts/power-moves",
      color: "text-primary",
      bgGradient: "from-primary/10 to-primary/5",
      benefit: "Create your advancement roadmap",
      time: "30 min"
    },
    {
      title: "OKRs",
      description: "Set measurable career goals and track your progress toward meaningful achievements.",
      icon: Target,
      path: "/artifacts/okrs",
      color: "text-success",
      bgGradient: "from-success/10 to-success/5",
      benefit: "Turn ambitions into achievements",
      time: "25 min"
    },
    {
      title: "Opportunity Audit",
      description: "Evaluate career opportunities with data-driven insights to make confident decisions.",
      icon: BarChart3,
      path: "/artifacts/opportunity-audit",
      color: "text-warning",
      bgGradient: "from-warning/10 to-warning/5",
      benefit: "Make smarter career choices",
      time: "40 min"
    },
  ];

  return (
    <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 md:py-12 overflow-x-hidden">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 md:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent px-2">
            Career Artifacts
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-text-secondary mb-6 sm:mb-8 px-2 leading-relaxed">
            Build powerful career tools that work together to accelerate your growth
          </p>
        </div>

        {/* Artifacts Grid */}
        <div className="grid gap-4 sm:gap-5 md:gap-6 grid-cols-1 md:grid-cols-2">
          {artifacts.map((artifact) => (
            <Link key={artifact.title} to={artifact.path} className="block">
              <Card className={`group p-4 sm:p-5 md:p-6 hover:shadow-lg transition-all duration-200 bg-gradient-to-br ${artifact.bgGradient} border-border/20 hover:border-primary/20 touch-target-min`}>
                <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className={`p-2 sm:p-3 rounded-lg bg-background/50 ${artifact.color} group-hover:scale-110 transition-transform duration-200 flex-shrink-0`}>
                    <artifact.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg md:text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                      {artifact.title}
                    </h3>
                    <p className="text-text-secondary text-xs sm:text-sm leading-relaxed">
                      {artifact.description}
                    </p>
                  </div>
                </div>
                
                {/* Benefit & CTA */}
                <div className="space-y-2 sm:space-y-3">
                  <div className="text-xs sm:text-sm font-medium text-primary">
                    ✨ {artifact.benefit}
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-border/20">
                    <span className="text-xs text-text-secondary">
                      About {artifact.time}
                    </span>
                    <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
                      <span className="text-xs sm:text-sm">Get started</span>
                      <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4" />
                    </div>
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-8 sm:mt-10 md:mt-12">
          <Link 
            to="/artifacts/saved"
            className="inline-flex items-center gap-2 text-text-secondary hover:text-primary transition-colors text-sm sm:text-base touch-target-min"
          >
            <FileText className="h-3 w-3 sm:h-4 sm:w-4" />
            View your saved artifacts
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CareerArtifactsPage;
